// Description: WordPress API functions
// Used to fetch data from a WordPress site using the WordPress REST API
// Types are imported from `wp.d.ts`

import querystring from 'query-string';

import {
  Author,
  Category,
  FeaturedMedia,
  Page,
  Post,
  Tag,
} from './wordpress.d';

// WordPress Config

const baseUrl = 'https://wordpress.qeeper.co.uk/';

function getUrl(path: string, query?: Record<string, any>) {
  const params = query ? querystring.stringify(query) : null;

  return `${baseUrl}${path}${params ? `?${params}` : ''}`;
}

// WordPress Functions
export async function getAllPosts(filterParams?: {
  author?: string;
  tag?: string;
  category?: string;
  per_page?: number;
  page?: number;
  fields?: string; // Add fields parameter for filtering
}): Promise<Post[]> {
  const url = getUrl('/wp-json/wp/v2/posts', {
    author: filterParams?.author,
    tags: filterParams?.tag,
    categories: filterParams?.category,
    per_page: filterParams?.per_page || 100,
    page: filterParams?.page || 1,
    _fields: filterParams?.fields, // WordPress API field filtering
  });

  // Use native fetch with Next.js caching options
  const response = await fetch(url, {
    next: {
      revalidate: 600, // Cache for 10 minutes
      tags: ['wordpress-posts'],
    },
  });

  const posts: Post[] = await response.json();
  return posts;
}

export async function getPostsCount(filterParams?: {
  author?: string;
  tag?: string;
  category?: string;
}): Promise<number> {
  const url = getUrl('/wp-json/wp/v2/posts', {
    author: filterParams?.author,
    tags: filterParams?.tag,
    categories: filterParams?.category,
    per_page: 1, // Only fetch 1 post to get the count from headers
    page: 1,
  });

  // Use native fetch with Next.js caching options
  const response = await fetch(url, {
    next: {
      revalidate: 600,
      tags: ['wordpress-posts-count'],
    },
  });

  // WordPress API returns total count in X-WP-Total header
  const totalCount = response.headers.get('X-WP-Total');
  return totalCount ? parseInt(totalCount, 10) : 0;
}

export async function getPostById(id: number): Promise<Post> {
  const url = getUrl(`posts/${id}`);
  const response = await fetch(url);
  const post: Post = await response.json();
  return post;
}

export async function getPostBySlug(slug: string): Promise<Post> {
  const url = getUrl('/wp-json/wp/v2/posts', { slug });
  const response = await fetch(url, {
    next: {
      revalidate: 600,
      tags: ['wordpress-post'],
    },
  });
  const post: Post[] = await response.json();
  return post[0];
}

export async function getAllCategories(): Promise<Category[]> {
  const url = getUrl('/wp-json/wp/v2/categories');
  const response = await fetch(url, {
    next: {
      revalidate: 600,
      tags: ['wordpress-categories'],
    },
  });
  const categories: Category[] = await response.json();
  return categories;
}

export async function getCategoryById(id: number): Promise<Category> {
  const url = getUrl(`/wp-json/wp/v2/categories/${id}`);
  const response = await fetch(url);
  const category: Category = await response.json();
  return category;
}

export async function getCategoryBySlug(slug: string): Promise<Category> {
  const url = getUrl('/wp-json/wp/v2/categories', { slug });
  const response = await fetch(url);
  const category: Category[] = await response.json();
  return category[0];
}
export async function getPostsByCategory(categoryId: number): Promise<Post[]> {
  const url = getUrl('/wp-json/wp/v2/posts', { categories: categoryId });
  const response = await fetch(url);
  const posts: Post[] = await response.json();
  return posts;
}

export async function getPostsByTag(tagId: number): Promise<Post[]> {
  const url = getUrl('/wp-json/wp/v2/posts', { tags: tagId });
  const response = await fetch(url);
  const posts: Post[] = await response.json();
  return posts;
}

export async function getTagsByPost(postId: number): Promise<Tag[]> {
  const url = getUrl('/wp-json/wp/v2/tags', { post: postId });
  const response = await fetch(url, {
    next: {
      revalidate: 600,
      tags: ['wordpress-tags'],
    },
  });
  const tags: Tag[] = await response.json();
  return tags;
}

export async function getAllTags(): Promise<Tag[]> {
  const url = getUrl('/wp-json/wp/v2/tags');
  const response = await fetch(url, {
    next: {
      revalidate: 600,
      tags: ['wordpress-tags'],
    },
  });
  const tags: Tag[] = await response.json();
  return tags;
}

export async function getTagById(id: number): Promise<Tag> {
  const url = getUrl(`/wp-json/wp/v2/tags/${id}`);
  const response = await fetch(url);
  const tag: Tag = await response.json();
  return tag;
}

export async function getTagBySlug(slug: string): Promise<Tag> {
  const url = getUrl('tags', { slug });
  const response = await fetch(url);
  const tag: Tag[] = await response.json();
  return tag[0];
}

export async function getAllPages(): Promise<Page[]> {
  const url = getUrl('pages');
  const response = await fetch(url);
  const pages: Page[] = await response.json();
  return pages;
}

export async function getPageById(id: number): Promise<Page> {
  const url = getUrl(`pages/${id}`);
  const response = await fetch(url);
  const page: Page = await response.json();
  return page;
}

export async function getPageBySlug(slug: string): Promise<Page> {
  const url = getUrl('pages', { slug });
  const response = await fetch(url);
  const page: Page[] = await response.json();
  return page[0];
}

export async function getAllAuthors(): Promise<Author[]> {
  const url = getUrl('users');
  const response = await fetch(url);
  const authors: Author[] = await response.json();
  return authors;
}

export async function getAuthorById(id: number): Promise<Author> {
  const url = getUrl(`users/${id}`);
  const response = await fetch(url);
  const author: Author = await response.json();
  return author;
}

export async function getAuthorBySlug(slug: string): Promise<Author> {
  const url = getUrl('users', { slug });
  const response = await fetch(url);
  const author: Author[] = await response.json();
  return author[0];
}

export async function getPostsByAuthor(authorId: number): Promise<Post[]> {
  const url = getUrl('posts', { author: authorId });
  const response = await fetch(url);
  const posts: Post[] = await response.json();
  return posts;
}

export async function getPostsByAuthorSlug(
  authorSlug: string,
): Promise<Post[]> {
  const author = await getAuthorBySlug(authorSlug);
  const url = getUrl('posts', { author: author.id });
  const response = await fetch(url);
  const posts: Post[] = await response.json();
  return posts;
}

export async function getPostsByCategorySlug(
  categorySlug: string,
): Promise<Post[]> {
  const category = await getCategoryBySlug(categorySlug);
  const url = getUrl('posts', { categories: category.id });
  const response = await fetch(url);
  const posts: Post[] = await response.json();
  return posts;
}

export async function getPostsByTagSlug(tagSlug: string): Promise<Post[]> {
  const tag = await getTagBySlug(tagSlug);
  const url = getUrl('posts', { tags: tag.id });
  const response = await fetch(url);
  const posts: Post[] = await response.json();
  return posts;
}

export async function getFeaturedMediaById(id: number): Promise<FeaturedMedia> {
  const url = getUrl(`media/${id}`);
  const response = await fetch(url);
  const featuredMedia: FeaturedMedia = await response.json();
  return featuredMedia;
}

export async function getPageByPath(
  slug: string,
  servicePath?: string,
): Promise<Page | null> {
  const url = getUrl('/wp-json/wp/v2/pages', { slug });
  const response = await fetch(url, {
    next: {
      revalidate: 600,
      tags: ['wordpress-page'],
    },
  });

  if (!response.ok) {
    return null;
  }

  const pages: Page[] = await response.json();

  if (pages.length === 0) {
    return null;
  }

  if (servicePath) {
    const servicePathInUrl = `/services/${servicePath}/`;
    return pages.find((page) => page.link.includes(servicePathInUrl)) ?? null;
  }

  return pages[0];
}
